/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 📄 File: components/auth/SessionExpiredOverlay.tsx
 🧩 Description:
    Non-dismissible overlay shown when the user's session expires.
    Listens for the global 'session:expired' CustomEvent dispatched
    by the Axios interceptor when both the access token and the
    refresh token have expired.
    
    Pattern: Event Bus bridge between imperative Axios interceptor
    and React — avoids calling hooks from outside the React tree.
────────────────────────────────────────────────────────────────*/

import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LockKeyhole } from 'lucide-react';
import { useAuthStore } from '../../store/auth';

export function SessionExpiredOverlay() {
    const { t } = useTranslation('auth');
    const navigate = useNavigate();
    const [visible, setVisible] = useState(false);
    const localLogout = useAuthStore((s) => s.localLogout);
    // Prevent duplicate firings when multiple fetch calls return 401 simultaneously
    const firedRef = useRef(false);

    useEffect(() => {
        const handleSessionExpired = () => {
            if (firedRef.current) return; // already handling
            firedRef.current = true;
            localLogout();
            setVisible(true);
        };

        window.addEventListener('session:expired', handleSessionExpired);
        return () => window.removeEventListener('session:expired', handleSessionExpired);
    }, [localLogout]);

    // ── Auto-redirect to login after 2.5s so the console 401 flood stops ──
    useEffect(() => {
        if (!visible) return;
        const timer = setTimeout(() => {
            navigate('/login', { replace: true });
        }, 2500);
        return () => clearTimeout(timer);
    }, [visible, navigate]);

    if (!visible) return null;

    const handleGoToLogin = () => {
        setVisible(false);
        navigate('/login', { replace: true });
    };

    return (
        // Full-screen backdrop — blocks all mouse events on the app below
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm"
            aria-modal="true"
            role="alertdialog"
            aria-labelledby="session-expired-title"
        >
            {/* Card */}
            <div className="animate-in fade-in zoom-in-95 duration-300 bg-card border border-border rounded-2xl shadow-2xl p-8 max-w-sm w-full mx-4 text-center">
                {/* Lock icon */}
                <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                    <LockKeyhole className="h-8 w-8 text-primary" />
                </div>

                <h2
                    id="session-expired-title"
                    className="text-xl font-bold text-foreground mb-3"
                >
                    {t('sessionExpired.title')}
                </h2>

                <p className="text-sm text-muted-foreground leading-relaxed mb-7">
                    {t('sessionExpired.message')}
                </p>

                <button
                    onClick={handleGoToLogin}
                    className="w-full h-10 px-6 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                >
                    {t('sessionExpired.button')}
                </button>
            </div>
        </div>
    );
}
